import { useState, useEffect, useMemo, useCallback } from "react";
import { tasksAPI, taskGroupsAPI, projectsAPI, revisionAPI, peopleAPI, attachmentsAPI } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import FileAttachments from "@/components/FileAttachments";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search,
  RefreshCw,
  ListTodo,
  ChevronRight,
  ChevronDown,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  User,
  Save,
  RotateCcw,
  History,
  Filter,
  Loader2,
  Paperclip,
  Lock
} from "lucide-react";
import { format, parseISO } from "date-fns";

const STATUS_COLORS = {
  "Not Started": "bg-slate-500",
  "In Progress": "bg-blue-500",
  "Completed": "bg-green-500",
  "Overdue": "bg-red-500",
  "Revision Required": "bg-amber-500",
  "Skipping": "bg-gray-400"
};

const TASK_STATUSES = ["Not Started", "In Progress", "Completed", "Overdue", "Revision Required", "Skipping"];
const REVISION_REQUESTERS = ["Customer", "OSR", "ISR", "Internal"];

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return "-";
  }
}

function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "yyyy-MM-dd");
  } catch {
    return "";
  }
}

export default function TasksPage() {
  const { user, canEdit } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [taskGroups, setTaskGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState({ osr: [], isr: [], engineering: [], management: [], design: [], operations: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [revisionFilter, setRevisionFilter] = useState("all"); // all, has_revisions, no_revisions
  const [projectFilter, setProjectFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({});
  
  // Changelog state
  const [taskChangelog, setTaskChangelog] = useState([]);
  const [loadingChangelog, setLoadingChangelog] = useState(false);
  
  // Revision dialog state
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [revisionTask, setRevisionTask] = useState(null);
  const [revisionForm, setRevisionForm] = useState({ days_added: 5, reason: "", requested_by: "Customer" });
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [revisionHistory, setRevisionHistory] = useState(null);
  const [revisionHistoryTaskId, setRevisionHistoryTaskId] = useState(null);
  const [deletingRevision, setDeletingRevision] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "Not Started",
    start_date: "",
    due_date: "",
    duration: 0,
    role: "",
    assigned_to: [],
    task_group_id: "",
    project_id: "",
    dates_locked: false
  });

  const fetchTaskChangelog = async (taskId) => {
    setLoadingChangelog(true);
    try {
      const res = await tasksAPI.getChangelog(taskId);
      setTaskChangelog(res.data?.data?.changes || []);
    } catch (e) {
      setTaskChangelog([]);
    }
    setLoadingChangelog(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // If user is not management, filter by their resource_id
      const resourceId = user && !user.is_management && user.resource_id ? user.resource_id : null;
      
      const [tasksRes, taskGroupsRes, projectsRes, peopleRes] = await Promise.all([
        tasksAPI.getAll(null, resourceId),
        taskGroupsAPI.getAll(),
        projectsAPI.getAll(resourceId),
        peopleAPI.getAll()
      ]);
      setTasks(tasksRes.data.data || []);
      setTaskGroups(taskGroupsRes.data.data || []);
      setProjects(projectsRes.data.data || []);
      setPeople(peopleRes.data.data || { osr: [], isr: [] });
    } catch (error) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  // Memoize filtered and sorted tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const name = task.fields?.["Task Name"] || "";
      const status = task.fields?.Status || "";
      const projectName = task.fields?.["Project Name"] || "";
      const role = task.fields?.Role || "";
      const revisions = task.fields?.revisions || [];
      const hasRevisions = revisions.length > 0;
      const searchLower = searchTerm.toLowerCase();
      
      const matchesSearch = name.toLowerCase().includes(searchLower) || 
                            projectName.toLowerCase().includes(searchLower) ||
                            role.toLowerCase().includes(searchLower);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesRevision = revisionFilter === "all" || 
                             (revisionFilter === "has_revisions" && hasRevisions) ||
                             (revisionFilter === "no_revisions" && !hasRevisions);
      const matchesProject = projectFilter === "all" || projectName === projectFilter;
      
      return matchesSearch && matchesStatus && matchesRevision && matchesProject;
    }).sort((a, b) => {
      // Sort by due date
      const dateA = a.fields?.["Due Date"] || "";
      const dateB = b.fields?.["Due Date"] || "";
      return dateA.localeCompare(dateB);
    });
  }, [tasks, searchTerm, statusFilter, revisionFilter, projectFilter]);
  
  // Memoize unique project names for filter
  const uniqueProjects = useMemo(() => 
    [...new Set(tasks.map(t => t.fields?.["Project Name"]).filter(Boolean))].sort(),
    [tasks]
  );
  
  // Memoize revision stats
  const tasksWithRevisions = useMemo(() => 
    tasks.filter(t => (t.fields?.revisions || []).length > 0),
    [tasks]
  );
  const totalRevisionDays = useMemo(() => 
    tasks.reduce((sum, t) => sum + (t.fields?.total_revision_days || 0), 0),
    [tasks]
  );

  // Revision functions
  const openRevisionDialog = useCallback((task) => {
    setRevisionTask(task);
    setRevisionForm({ days_added: 5, reason: "", requested_by: "Customer" });
    setShowRevisionDialog(true);
  }, []);

  const submitRevision = async () => {
    if (!revisionTask || !revisionForm.reason.trim()) {
      toast.error("Please provide a reason for the revision");
      return;
    }
    
    setSubmittingRevision(true);
    try {
      const response = await revisionAPI.request(revisionTask.id, revisionForm);
      if (response.data.success) {
        const result = response.data.data;
        toast.success(
          `Revision #${result.revision_number} added: +${result.days_added} days`
        );
        setShowRevisionDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to request revision");
    } finally {
      setSubmittingRevision(false);
    }
  };

  const viewRevisionHistory = async (task) => {
    try {
      const response = await revisionAPI.getHistory(task.id);
      if (response.data.success) {
        setRevisionHistory(response.data.data);
        setRevisionHistoryTaskId(task.id);
        setShowRevisionHistory(true);
      }
    } catch (error) {
      toast.error("Failed to load revision history");
    }
  };

  const deleteRevision = async (revisionNumber) => {
    if (!revisionHistoryTaskId) return;
    
    setDeletingRevision(revisionNumber);
    try {
      const response = await revisionAPI.delete(revisionHistoryTaskId, revisionNumber);
      if (response.data.success) {
        toast.success(`Revision #${revisionNumber} deleted`);
        // Refresh revision history
        const historyResponse = await revisionAPI.getHistory(revisionHistoryTaskId);
        if (historyResponse.data.success) {
          setRevisionHistory(historyResponse.data.data);
        }
        // Refresh tasks to update the count
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete revision");
    } finally {
      setDeletingRevision(null);
    }
  };

  const toggleTask = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const getTaskGroupName = (taskGroupIds) => {
    if (!taskGroupIds || taskGroupIds.length === 0) return "-";
    const group = taskGroups.find(g => taskGroupIds.includes(g.id));
    return group?.fields?.["Task Group Name"] || "-";
  };

  const getProjectName = (projectName) => {
    return projectName || "-";
  };

  const openCreateDialog = () => {
    setSelectedTask(null);
    setFormData({
      title: "",
      description: "",
      status: "Not Started",
      start_date: "",
      due_date: "",
      duration: 0,
      role: "",
      assigned_to: [],
      task_group_id: "",
      project_id: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (task, e) => {
    e?.stopPropagation();
    setSelectedTask(task);
    setFormData({
      title: task.fields?.["Task Name"] || "",
      description: task.fields?.Description || "",
      status: task.fields?.Status || "Not Started",
      start_date: formatDateForInput(task.fields?.["Start Date"]),
      due_date: formatDateForInput(task.fields?.["Due Date"]),
      duration: task.fields?.["Task Duration"] || 0,
      role: task.fields?.Role || "",
      assigned_to: task.fields?.["Assigned To"] || [],
      task_group_id: task.fields?.["Task Group"]?.[0] || "",
      project_id: task.fields?.Projects?.[0] || "",
      dates_locked: task.fields?.dates_locked || false
    });
    setDialogOpen(true);
    // Fetch changelog for this task
    fetchTaskChangelog(task.id);
  };

  const openDeleteDialog = (task, e) => {
    e?.stopPropagation();
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedTask) {
        await tasksAPI.update(selectedTask.id, formData);
        toast.success("Task updated successfully");
      } else {
        await tasksAPI.create(formData);
        toast.success("Task created successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(selectedTask ? "Failed to update task" : "Failed to create task");
    }
  };

  const handleDelete = async () => {
    try {
      await tasksAPI.delete(selectedTask.id);
      toast.success("Task deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const quickUpdateStatus = async (task, newStatus, e) => {
    e?.stopPropagation();
    try {
      await tasksAPI.update(task.id, { status: newStatus });
      // Update local state instead of refetching
      setTasks(prevTasks => 
        prevTasks.map(t => 
          t.id === task.id 
            ? { ...t, fields: { ...t.fields, Status: newStatus } }
            : t
        )
      );
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground mt-1">Manage all tasks - click to expand details</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-tasks-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    onClick={openCreateDialog} 
                    data-testid="create-task-btn"
                    disabled={!canEdit}
                  >
                    {!canEdit && <Lock className="h-4 w-4 mr-2" />}
                    {canEdit && <Plus className="h-4 w-4 mr-2" />}
                    New Task
                  </Button>
                </span>
              </TooltipTrigger>
              {!canEdit && (
                <TooltipContent>
                  <p>Management access required</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-tasks-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {TASK_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={revisionFilter} onValueChange={setRevisionFilter}>
              <SelectTrigger className="w-40" data-testid="revision-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Has Revisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="has_revisions">Has Revisions</SelectItem>
                <SelectItem value="no_revisions">No Revisions</SelectItem>
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-40" data-testid="project-filter">
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {uniqueProjects.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="w-fit">
              {filteredTasks.length} task{filteredTasks.length !== 1 && "s"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : filteredTasks.length > 0 ? (
            <div className="space-y-2">
              {filteredTasks.map((task, index) => {
                const isExpanded = expandedTasks[task.id];
                const rawStatus = task.fields?.Status || "Not Started";
                const isCompleted = rawStatus === "Completed" || rawStatus === "Complete" || rawStatus === "Skipping";
                const dueDate = task.fields?.["Due Date"];
                // Compute overdue: not completed, has due date, due date is in the past
                const isOverdue = !isCompleted && dueDate && new Date(dueDate) < new Date(new Date().toDateString()) && rawStatus !== "Overdue" && rawStatus !== "Revision Required";
                const status = isOverdue ? "Overdue" : rawStatus;
                
                return (
                  <Collapsible 
                    key={task.id}
                    open={isExpanded}
                    onOpenChange={() => toggleTask(task.id)}
                  >
                    <div 
                      className="border rounded-lg overflow-hidden animate-slide-in"
                      style={{ animationDelay: `${Math.min(index, 20) * 0.02}s` }}
                      onDoubleClick={() => openEditDialog(task)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className={`flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors ${isCompleted ? "opacity-60" : ""}`}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            )}
                            {task.fields?.dates_locked && <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" title="Dates locked" />}
                            {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                            {isOverdue && <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                              <div className={`font-medium truncate ${isCompleted ? "line-through" : ""}`}>
                                {task.fields?.["Task Name"] || "Untitled Task"}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {getProjectName(task.fields?.["Project Name"])} • {getTaskGroupName(task.fields?.["Task Group"])}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(task.fields?.["Due Date"])}</span>
                            </div>
                            
                            <div onClick={(e) => e.stopPropagation()}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Select 
                                        value={status} 
                                        onValueChange={(val) => quickUpdateStatus(task, val)}
                                        disabled={!canEdit}
                                      >
                                        <SelectTrigger className={`h-7 w-28 text-xs ${!canEdit ? 'opacity-70' : ''}`}>
                                          <Badge className={`${STATUS_COLORS[status]} text-xs`}>{status}</Badge>
                                        </SelectTrigger>
                                        <SelectContent>
                                          {TASK_STATUSES.map(s => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </span>
                                  </TooltipTrigger>
                                  {!canEdit && (
                                    <TooltipContent>
                                      <p>Management access required</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={(e) => openEditDialog(task, e)}
                                        data-testid={`edit-task-${task.id}`}
                                        disabled={!canEdit}
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!canEdit && (
                                    <TooltipContent>
                                      <p>Management access required</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-destructive hover:text-destructive"
                                        onClick={(e) => openDeleteDialog(task, e)}
                                        data-testid={`delete-task-${task.id}`}
                                        disabled={!canEdit}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!canEdit && (
                                    <TooltipContent>
                                      <p>Management access required</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="border-t bg-muted/20 p-4">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Start Date</div>
                              <div className="font-medium">{formatDate(task.fields?.["Start Date"])}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Due Date</div>
                              <div className="font-medium flex items-center gap-1">
                                {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
                                {formatDate(task.fields?.["Due Date"])}
                              </div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Duration</div>
                              <div className="font-medium">{task.fields?.["Task Duration"] || 0} days</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Role</div>
                              <div className="font-medium">{task.fields?.Role || "-"}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Assigned To</div>
                              <div className="font-medium">
                                {(() => {
                                  const assignedTo = task.fields?.["Assigned To"] || [];
                                  if (assignedTo.length === 0) return <span className="text-muted-foreground">Unassigned</span>;
                                  const names = assignedTo.map(id => {
                                    const osr = people.osr?.find(p => p.id === id);
                                    if (osr) return osr.name;
                                    const isr = people.isr?.find(p => p.id === id);
                                    if (isr) return isr.name;
                                    return null;
                                  }).filter(Boolean);
                                  return names.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {names.map((name, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs">{name}</Badge>
                                      ))}
                                    </div>
                                  ) : <span className="text-muted-foreground">Unassigned</span>;
                                })()}
                              </div>
                            </div>
                            <div>
                              <div className="text-muted-foreground text-xs mb-1">Revisions</div>
                              <div className="font-medium flex items-center gap-2">
                                {(task.fields?.revisions || []).length > 0 ? (
                                  <>
                                    <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                                      <History className="h-3 w-3 mr-1" />
                                      {task.fields.revisions.length}
                                    </Badge>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={(e) => { e.stopPropagation(); viewRevisionHistory(task); }}
                                      data-testid={`view-revisions-${task.id}`}
                                    >
                                      View
                                    </Button>
                                  </>
                                ) : (
                                  <span className="text-muted-foreground">None</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {task.fields?.["Task Order"] && (
                            <div className="mt-3 pt-3 border-t">
                              <div className="text-muted-foreground text-xs">Task Order: #{task.fields?.["Task Order"]}</div>
                            </div>
                          )}
                          {/* Request Revision Button */}
                          <div className="mt-3 pt-3 border-t flex justify-between items-start">
                            {/* Attachments Section */}
                            <div className="flex-1 mr-4">
                              <div className="text-muted-foreground text-xs mb-2 flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                Attachments
                              </div>
                              <FileAttachments 
                                taskId={task.id}
                                attachments={task.fields?.attachments || []}
                                onUpdate={fetchData}
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openRevisionDialog(task); }}
                              data-testid={`request-revision-${task.id}`}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Request Revision
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <ListTodo className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No tasks found</p>
              <p className="text-sm">Create your first task to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>
              {selectedTask ? "Edit Task" : "Create New Task"}
            </DialogTitle>
            <DialogDescription>
              {selectedTask ? "Update task details and view change history" : "Fill in the details to create a new task"}
            </DialogDescription>
          </DialogHeader>
          {selectedTask ? (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="changelog" className="flex items-center gap-1">
                  <History className="h-3 w-3" /> Change Log
                </TabsTrigger>
              </TabsList>
              <TabsContent value="details">
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Task Name *</Label>
                      <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Enter task name" required data-testid="task-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                          <SelectTrigger data-testid="task-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                          <SelectContent>{TASK_STATUSES.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration">Duration (days)</Label>
                        <Input id="duration" type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })} data-testid="task-duration-input" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="start_date">Start Date</Label>
                        <Input id="start_date" type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} data-testid="task-start-date-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="due_date">Due Date</Label>
                        <Input id="due_date" type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="task-due-date-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Input id="role" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="e.g., Engineering, OSR, ISR" data-testid="task-role-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assigned To</Label>
                      <Select value={formData.assigned_to?.[0] || "none"} onValueChange={(v) => setFormData({ ...formData, assigned_to: v === "none" ? [] : [v] })}>
                        <SelectTrigger data-testid="task-assigned-to-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {people.all?.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="task_group">Task Group</Label>
                      <Select value={formData.task_group_id || "none"} onValueChange={(value) => setFormData({ ...formData, task_group_id: value === "none" ? "" : value })}>
                        <SelectTrigger data-testid="task-group-select"><SelectValue placeholder="Select task group" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {taskGroups.map(group => (<SelectItem key={group.id} value={group.id}>{group.fields?.["Task Group Name"] || "Unnamed"}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                      <Checkbox 
                        id="dates-locked" 
                        checked={formData.dates_locked || false}
                        onCheckedChange={(checked) => setFormData({ ...formData, dates_locked: checked })}
                      />
                      <div className="grid gap-0.5 leading-none">
                        <Label htmlFor="dates-locked" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                          <Lock className="h-3 w-3" /> Lock Dates
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Prevent date cascade from changing this task's dates
                        </p>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="save-task-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
                  </DialogFooter>
                </form>
              </TabsContent>
              <TabsContent value="changelog" className="py-4">
                <ScrollArea className="h-[300px] rounded-md border">
                  {loadingChangelog ? (
                    <div className="flex items-center justify-center h-full">
                      <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : taskChangelog.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <History className="h-8 w-8 mb-2" />
                      <p>No changes recorded yet</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      {taskChangelog.map((change, idx) => {
                        const changeDate = change.changed_at ? new Date(change.changed_at) : null;
                        const formattedDate = changeDate ? format(changeDate, "MMM d, yyyy h:mm a") : "Unknown";
                        const changeTypeColors = {
                          "status_change": "bg-blue-100 text-blue-700",
                          "assignment": "bg-purple-100 text-purple-700",
                          "date_change": "bg-amber-100 text-amber-700",
                          "update": "bg-gray-100 text-gray-700"
                        };
                        const badgeClass = changeTypeColors[change.change_type] || changeTypeColors.update;
                        
                        return (
                          <div key={idx} className="border-l-2 border-muted pl-3 pb-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={badgeClass + " text-xs"}>{change.field_name}</Badge>
                              <span className="text-xs text-muted-foreground">{formattedDate}</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-muted-foreground line-through mr-2">{change.old_value || "(empty)"}</span>
                              <span className="text-foreground">→ {change.new_value || "(empty)"}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">by {change.changed_by}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Task Name *</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Enter task name" required data-testid="task-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger data-testid="task-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                      <SelectContent>{TASK_STATUSES.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (days)</Label>
                    <Input id="duration" type="number" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 0 })} data-testid="task-duration-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input id="start_date" type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} data-testid="task-start-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input id="due_date" type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} data-testid="task-due-date-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input id="role" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="e.g., Engineering, OSR, ISR" data-testid="task-role-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assigned_to">Assigned To</Label>
                  <Select value={formData.assigned_to?.[0] || "none"} onValueChange={(v) => setFormData({ ...formData, assigned_to: v === "none" ? [] : [v] })}>
                    <SelectTrigger data-testid="task-assigned-to-select"><SelectValue placeholder="Select assignee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {people.all?.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task_group">Task Group</Label>
                  <Select value={formData.task_group_id || "none"} onValueChange={(value) => setFormData({ ...formData, task_group_id: value === "none" ? "" : value })}>
                    <SelectTrigger data-testid="task-group-select"><SelectValue placeholder="Select task group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {taskGroups.map(group => (<SelectItem key={group.id} value={group.id}>{group.fields?.["Task Group Name"] || "Unnamed"}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-task-btn"><Save className="h-4 w-4 mr-1" /> {selectedTask ? "Save" : "Create"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTask?.fields?.["Task Name"]}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-task-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Request Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Request Revision
            </DialogTitle>
            <DialogDescription>
              Request a revision for "{revisionTask?.fields?.["Task Name"]}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="days_added">Days to Add *</Label>
              <Input
                id="days_added"
                type="number"
                min="1"
                value={revisionForm.days_added}
                onChange={(e) => setRevisionForm({ ...revisionForm, days_added: parseInt(e.target.value) || 0 })}
                data-testid="revision-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="requested_by">Requested By *</Label>
              <Select
                value={revisionForm.requested_by}
                onValueChange={(value) => setRevisionForm({ ...revisionForm, requested_by: value })}
              >
                <SelectTrigger data-testid="revision-requester-select">
                  <SelectValue placeholder="Select requester" />
                </SelectTrigger>
                <SelectContent>
                  {REVISION_REQUESTERS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                value={revisionForm.reason}
                onChange={(e) => setRevisionForm({ ...revisionForm, reason: e.target.value })}
                placeholder="Describe the reason for the revision..."
                rows={3}
                data-testid="revision-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitRevision} 
              disabled={submittingRevision || !revisionForm.reason.trim()}
              data-testid="submit-revision-btn"
            >
              {submittingRevision ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-1" />
              )}
              Request Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog open={showRevisionHistory} onOpenChange={setShowRevisionHistory}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-amber-500" />
              Revision History
            </DialogTitle>
            <DialogDescription>
              {revisionHistory?.task_name && `Revisions for "${revisionHistory.task_name}"`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {revisionHistory?.revisions?.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">Total Revisions</div>
                    <div className="text-xl font-bold text-amber-600">{revisionHistory.revisions.length}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Total Days Added</div>
                    <div className="text-xl font-bold text-amber-600">
                      +{revisionHistory.revisions.reduce((sum, r) => sum + (r.days_added || 0), 0)} days
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Days Added</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-[60px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {revisionHistory.revisions.map((rev, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{rev.revision_number || idx + 1}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{rev.requested_by}</Badge>
                        </TableCell>
                        <TableCell className="text-amber-600 font-medium">+{rev.days_added} days</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(rev.created_at)}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate" title={rev.reason}>
                          {rev.reason}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => deleteRevision(rev.revision_number || idx + 1)}
                            disabled={deletingRevision === (rev.revision_number || idx + 1)}
                            data-testid={`delete-revision-${rev.revision_number || idx + 1}`}
                          >
                            {deletingRevision === (rev.revision_number || idx + 1) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No revision history found</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionHistory(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
